/**
 * ExpenseVoucherPage — Elszámolásra Kiadás
 * Story 62-8: Kiadási pénztárbizonylat
 * Story 62-9: Elszámolásra kiadás kezelés (3 munkanap deadline)
 *
 * CRUD page: list, issue, settle, cancel expense vouchers.
 * RBAC: BOLTVEZETO+ (Level 2)
 */

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  useCancelExpenseVoucher,
  useExpenseVouchers,
  useIssueExpenseVoucher,
  useSettleExpenseVoucher,
} from '@/hooks/useCashManagement';
import { useTranslation } from '@kgc/i18n';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

type VoucherStatus = 'ISSUED' | 'SETTLED' | 'OVERDUE' | 'CANCELLED';

function getStatusBadgeClasses(status: VoucherStatus): string {
  switch (status) {
    case 'ISSUED':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
    case 'SETTLED':
      return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
    case 'OVERDUE':
      return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
    case 'CANCELLED':
      return 'bg-muted text-muted-foreground';
    default:
      return 'bg-muted text-muted-foreground';
  }
}

export function ExpenseVoucherPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // State
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [showIssueForm, setShowIssueForm] = useState(false);
  const [settleId, setSettleId] = useState<string | null>(null);

  // Form state — issue
  const [recipientId, setRecipientId] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [amount, setAmount] = useState('');
  const [purpose, setPurpose] = useState('');

  // Form state — settle
  const [settledAmount, setSettledAmount] = useState('');
  const [returnedAmount, setReturnedAmount] = useState('');
  const [receiptAttached, setReceiptAttached] = useState(false);

  // Queries & mutations
  const { vouchers, isLoading, error, refetch } = useExpenseVouchers(filterStatus || undefined);
  const issueMutation = useIssueExpenseVoucher();
  const settleMutation = useSettleExpenseVoucher();
  const cancelMutation = useCancelExpenseVoucher();

  const handleIssue = useCallback(async () => {
    if (!recipientName.trim() || !amount || !purpose.trim()) return;

    await issueMutation.mutateAsync({
      recipientId: recipientId || crypto.randomUUID(),
      recipientName: recipientName.trim(),
      amount: parseInt(amount, 10),
      purpose: purpose.trim(),
    });

    // Reset form
    setRecipientId('');
    setRecipientName('');
    setAmount('');
    setPurpose('');
    setShowIssueForm(false);
  }, [recipientId, recipientName, amount, purpose, issueMutation]);

  const handleSettle = useCallback(async () => {
    if (!settleId || !settledAmount) return;

    const data: {
      id: string;
      settledAmount: number;
      returnedAmount?: number;
      receiptAttached?: boolean;
    } = {
      id: settleId,
      settledAmount: parseInt(settledAmount, 10),
      receiptAttached,
    };
    if (returnedAmount) {
      data.returnedAmount = parseInt(returnedAmount, 10);
    }

    await settleMutation.mutateAsync(data);

    setSettleId(null);
    setSettledAmount('');
    setReturnedAmount('');
    setReceiptAttached(false);
  }, [settleId, settledAmount, returnedAmount, receiptAttached, settleMutation]);

  const handleCancel = useCallback(
    async (id: string) => {
      await cancelMutation.mutateAsync(id);
    },
    [cancelMutation]
  );

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('hu-HU', {
      style: 'currency',
      currency: 'HUF',
      maximumFractionDigits: 0,
    }).format(value);

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('hu-HU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">{t('finance.expenseVouchersTitle')}</h1>
        <div className="flex gap-2">
          <Button variant="default" onClick={() => setShowIssueForm(!showIssueForm)}>
            {t('finance.ev.issueNew')}
          </Button>
          <Button variant="outline" onClick={() => navigate('/finance/cash')}>
            {t('finance.back')}
          </Button>
        </div>
      </div>

      {/* Issue Form */}
      {showIssueForm && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>{t('finance.ev.issueTitle')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  {t('finance.ev.recipientName')}
                </label>
                <Input
                  value={recipientName}
                  onChange={e => setRecipientName(e.target.value)}
                  placeholder={t('finance.ev.recipientNamePlaceholder')}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  {t('finance.ev.amount')}
                </label>
                <Input
                  type="number"
                  value={amount}
                  onChange={e => setAmount(e.target.value)}
                  placeholder={t('finance.amountPlaceholder')}
                  min="1"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-foreground mb-1">
                  {t('finance.ev.purpose')}
                </label>
                <Input
                  value={purpose}
                  onChange={e => setPurpose(e.target.value)}
                  placeholder={t('finance.ev.purposePlaceholder')}
                />
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <Button
                onClick={handleIssue}
                disabled={
                  !recipientName.trim() || !amount || !purpose.trim() || issueMutation.isPending
                }
              >
                {issueMutation.isPending ? t('common.saving') : t('finance.ev.issue')}
              </Button>
              <Button variant="outline" onClick={() => setShowIssueForm(false)}>
                {t('common.cancel')}
              </Button>
            </div>
            {issueMutation.error && (
              <p className="text-destructive text-sm mt-2">{issueMutation.error.message}</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Settle Dialog */}
      {settleId && (
        <Card className="mb-6 border-primary">
          <CardHeader>
            <CardTitle>{t('finance.ev.settleTitle')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  {t('finance.ev.settledAmount')}
                </label>
                <Input
                  type="number"
                  value={settledAmount}
                  onChange={e => setSettledAmount(e.target.value)}
                  placeholder={t('finance.amountPlaceholder')}
                  min="1"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  {t('finance.ev.returnedAmount')}
                </label>
                <Input
                  type="number"
                  value={returnedAmount}
                  onChange={e => setReturnedAmount(e.target.value)}
                  placeholder="0"
                  min="0"
                />
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={receiptAttached}
                    onChange={e => setReceiptAttached(e.target.checked)}
                    className="rounded"
                  />
                  <span className="text-sm text-foreground">{t('finance.ev.receiptAttached')}</span>
                </label>
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <Button onClick={handleSettle} disabled={!settledAmount || settleMutation.isPending}>
                {settleMutation.isPending ? t('common.saving') : t('finance.ev.settle')}
              </Button>
              <Button variant="outline" onClick={() => setSettleId(null)}>
                {t('common.cancel')}
              </Button>
            </div>
            {settleMutation.error && (
              <p className="text-destructive text-sm mt-2">{settleMutation.error.message}</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Filter */}
      <div className="flex items-center gap-4 mb-4">
        <label className="text-sm font-medium text-foreground">
          {t('finance.ev.filterStatus')}
        </label>
        <select
          value={filterStatus}
          onChange={e => setFilterStatus(e.target.value)}
          className="rounded border px-3 py-1.5 text-sm bg-background text-foreground"
        >
          <option value="">{t('common.all')}</option>
          <option value="ISSUED">{t('finance.ev.statusIssued')}</option>
          <option value="OVERDUE">{t('finance.ev.statusOverdue')}</option>
          <option value="SETTLED">{t('finance.ev.statusSettled')}</option>
          <option value="CANCELLED">{t('finance.ev.statusCancelled')}</option>
        </select>
        <Button variant="ghost" size="sm" onClick={() => refetch()}>
          {t('common.refresh')}
        </Button>
      </div>

      {/* Error */}
      {error && (
        <p className="text-destructive mb-4">{t('finance.errorOccurred', { message: error })}</p>
      )}

      {/* Loading */}
      {isLoading && <p className="text-muted-foreground">{t('finance.loadingData')}</p>}

      {/* Voucher List */}
      {!isLoading && vouchers.length === 0 && (
        <Card>
          <CardContent className="p-8 text-center text-muted-foreground">
            {t('finance.ev.noVouchers')}
          </CardContent>
        </Card>
      )}

      {!isLoading && vouchers.length > 0 && (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                      {t('finance.ev.voucherNumber')}
                    </th>
                    <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                      {t('finance.ev.recipient')}
                    </th>
                    <th className="px-4 py-3 text-right font-medium text-muted-foreground">
                      {t('finance.ev.amount')}
                    </th>
                    <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                      {t('finance.ev.purpose')}
                    </th>
                    <th className="px-4 py-3 text-center font-medium text-muted-foreground">
                      {t('finance.status')}
                    </th>
                    <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                      {t('finance.ev.deadline')}
                    </th>
                    <th className="px-4 py-3 text-center font-medium text-muted-foreground">
                      {t('common.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {vouchers.map(v => (
                    <tr key={v.id} className="border-b hover:bg-muted/30">
                      <td className="px-4 py-3 font-mono text-xs">{v.voucherNumber}</td>
                      <td className="px-4 py-3">{v.recipientName}</td>
                      <td className="px-4 py-3 text-right font-medium">
                        {formatCurrency(v.amount)}
                      </td>
                      <td className="px-4 py-3 max-w-[200px] truncate">{v.purpose}</td>
                      <td className="px-4 py-3 text-center">
                        <span
                          className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeClasses(v.status)}`}
                        >
                          {t(
                            `finance.ev.status${v.status.charAt(0) + v.status.slice(1).toLowerCase()}`
                          )}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span className={v.isOverdue ? 'text-destructive font-medium' : ''}>
                          {formatDate(v.deadline)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex gap-1 justify-center">
                          {(v.status === 'ISSUED' || v.status === 'OVERDUE') && (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setSettleId(v.id);
                                  setSettledAmount(String(v.amount));
                                }}
                              >
                                {t('finance.ev.settle')}
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleCancel(v.id)}
                                disabled={cancelMutation.isPending}
                              >
                                {t('finance.ev.cancel')}
                              </Button>
                            </>
                          )}
                          {v.status === 'SETTLED' && v.receiptAttached && (
                            <span className="text-xs text-muted-foreground">
                              {t('finance.ev.receiptOk')}
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
